import { Common, Company, color } from '@stockbit/ui';
import number from 'utils/number';
import numeral from 'numeral';
import Link from 'next/link';
import { convertToSlug } from 'utils/general';
import { Fragment, useState } from 'react';
import companyUtils from 'utils/company';
import { Tooltip } from 'antd';
import IndicatorBar from 'global/Notation/IndicatorBar';
import CorpActionBar from 'global/CorpAction/CorpActionBar';
import { renderTags } from 'utils/tags';
import {
  Header,
  CountryFlag,
  ImagePlaceholder,
  Symbol,
  CountryFlagContainer,
  NotationCountryFlagContainer,
  NotationContainer,
  Badges,
  CompanyName,
  CompanyNameContainer,
  CompanyTags,
  ImageWrapper,
  CompanyInfoWrapper,
  ButtonFollow,
  ColumnMobile,
  Percentage,
  Value,
  CompanyHeader,
  ExpandButtonToggleMobile,
  NotationCorpActionBarWrapper,
} from './styled';

const { cleanSymbol } = companyUtils;

const { Flex, Text } = Common;
const {
  TagCompany: { SymbolTag },
} = Company;

interface CompanyChartProps {
  symbol: string;
  company: any;
  onClickLink?: () => void;
}

const STATUS_LIST = {
  SUSPENDED: 'STATUS_SUSPENDED',
  DELISTED: 'STATUS_DELISTED',
};

const COMPANY_TYPE_STOCK = 'saham';

function GuestHeader({ symbol = '', company, onClickLink }: CompanyChartProps) {
  const {
    name,
    sector,
    volume,
    average,
    price,
    change,
    percentage,
    time,
    followers,
    icon_url,
    country = 'ID',
    notation = [],
    uma,
    corp_action = {},
    status,
    catalogs = [],
    exchange,
    type_company = '',
  } = company;
  const [isIconAvailable, setIsIconAvailable] = useState(!!icon_url);
  const isNotationAvailable = !!notation.length;
  const isCorpActionAvailable = corp_action?.active;
  const isSuspended = status === STATUS_LIST.SUSPENDED;
  const isDelisted = status === STATUS_LIST.DELISTED;
  const isCompanyStock = type_company?.toLowerCase() === COMPANY_TYPE_STOCK;
  const POPUP_COUNTRY_FLAG_TEXT = `This stock is traded on ${exchange} exchange`;

  const onImgError = () => {
    setIsIconAvailable(false);
  };

  const formattedCountry = country.toLowerCase();
  const IMAGE_URL = `/img/flags/${formattedCountry}.png`;

  const getSectorURL = (sector, subsector) => (!sector
    ? ''
    : sector && sector.toLowerCase() !== 'indeks'
      ? `/catalog/${
        convertToSlug(sector)
      }/${
        convertToSlug(subsector)}`
      : `/catalog/${convertToSlug(subsector)}`);

  const onClickCatalogsLink = (event) => {
    event.preventDefault();
    onClickLink();
  };

  const renderCatalogsLink = (catalogue) => {
    const subSectorObject = {
      'Properti & Real Estate': 'Properti dan Real Estate',
      Commodities: 'Commodity',
    };
    const companySymbol = catalogue.catalog_name || catalogue.company_symbol;
    const companySymbolAlias = subSectorObject[companySymbol] || catalogue.company_symbol;
    const companyType = catalogue.company_type;
    if (companyType === 'sub_sector') {
      return (
        <Link href={getSectorURL(sector, companySymbolAlias)} passHref>
          <a target="_blank" rel="noopener noreferrer" onClick={onClickCatalogsLink}>
            {companySymbol}
          </a>
        </Link>
      );
    }

    return (
      <Link
        href={`/catalog/${companyType}/${companySymbolAlias
        }`}
        passHref
      >
        <a target="_blank" rel="noopener noreferrer" onClick={onClickCatalogsLink}>
          {companySymbol}
        </a>
      </Link>
    );
  };

  const renderSymbol = () => (
    <Symbol>{ cleanSymbol(symbol.toUpperCase()) }</Symbol>
  );

  const renderCompanyLogo = () => (
    <ImageWrapper align="center">
      {isIconAvailable ?
        <img className="company-header-icon" src={icon_url} onError={onImgError} alt={symbol} width="54px" height="54px" />
        :
        <ImagePlaceholder><small>{!!symbol && symbol.toUpperCase().slice(0, 1)}</small></ImagePlaceholder>
        }
    </ImageWrapper>
  );

  const renderNotationCorpActionBadges = () => (
    <NotationContainer>
      { renderTags({ symbol, uma, notation, corp_action }) }
    </NotationContainer>
  );

  const renderCountryFlag = () => (
    <CountryFlagContainer>
      <CountryFlag className="mobile" key="country-flag-mobile" src={IMAGE_URL} alt={`${country} flag`} />
      <Tooltip
        className="desktop"
        placement="top"
        title={POPUP_COUNTRY_FLAG_TEXT}
      >
        <CountryFlag key="country-flag-desktop" src={IMAGE_URL} alt={`${country} flag`} />
      </Tooltip>
    </CountryFlagContainer>
  );

  const renderSuspendedDelistedBadges = () => (
    <>
      {isSuspended && <Badges><span>Suspended</span></Badges>}
      {isDelisted && <Badges><span>Delisted</span></Badges>}
    </>
  );

  const renderCompanyName = () => (
    <CompanyName>
      <h1>{name}</h1>
    </CompanyName>
  );

  const renderCompanyTags = () => (
    <CompanyTags>
      {
          catalogs.slice(0, 6).map((catalogue) => (
            <span key={catalogue.company_symbol} className="catalogs">
              {renderCatalogsLink(catalogue)}
            </span>
          ))
        }
    </CompanyTags>
  );

  const renderExpandButtonToggle = () => (<ExpandButtonToggleMobile onExpand={onClickCatalogsLink} />);

  const renderNotationCorpActionBar = (isCompanyStock: boolean) => (
    <NotationCorpActionBarWrapper isCompanyStock={isCompanyStock} isBoth={isNotationAvailable && isCorpActionAvailable}>
      {isNotationAvailable && (
      <IndicatorBar
        symbol={symbol}
        notation={notation}
        uma={uma}
        name={name}
        isGuestCompanyStock={isCompanyStock}
        onClick={onClickCatalogsLink}
      />
      )}

      {isCorpActionAvailable && (
      <CorpActionBar
        symbol={symbol}
        corpAction={corp_action}
        isGuestCompanyStock={isCompanyStock}
        onClick={onClickCatalogsLink}
      />
      )}
    </NotationCorpActionBarWrapper>
  );

  const renderNonCompanyHeader = () => (
    <Header>
      <Flex align="center">
        <SymbolTag>{symbol.toUpperCase()}</SymbolTag>
      </Flex>

      <Flex justify="space-between" mt="10px">
        <ColumnMobile className="start-center">
          <Text size="24px" family="bold" mr="10px" style={{ flex: 1 }}>
            {name}
          </Text>
          <Text color={color.primary} size="14px" mr="10px">
            {sector}
          </Text>
        </ColumnMobile>
        <Link href="/register">
          <ButtonFollow height="30px" as="a">
            <Text padding="0 0 0 5px" color={color.dimgray} family="bold">
              Follow
            </Text>
          </ButtonFollow>
        </Link>
      </Flex>
      <Flex align="center" mt="20px" mb="20px">
        <Flex as="div" direction="column" mr="20px">
          <ColumnMobile className="start-center">
            <Value>{numeral(price).format('0,0.00')}</Value>
            <Percentage color={change > 0 ? color.primary : color.sunsetOrange}>
              {change > 0 ? <>&#9652;</> : <>&#9662;</>}{' '}
              {numeral(change || 0).format('0,0.00')} ({percentage})
            </Percentage>
          </ColumnMobile>
          <Text size="13px" color={color.midgray} mt="4px">
            As of {time}
          </Text>
        </Flex>
        <Flex as="div" direction="column" mr="20px">
          <Value>{number.kNumberFormatter(volume, 2, true, true)}</Value>
          <Text size="13px" color={color.midgray} mt="4px">
            Volume
          </Text>
        </Flex>
        <Flex as="div" direction="column">
          <Value>{number.kNumberFormatter(average, 2, true, true)}</Value>
          <Text size="13px" color={color.midgray} mt="4px">
            Avg Volume
          </Text>
        </Flex>
      </Flex>
      <div>
        <Text size="14px" color={color.dimgray} family="bold" as="span">
          {numeral(followers || 0).format('0,0')}
        </Text>{' '}
        <Text size="14px" color={color.midgray} as="span">
          Followers
        </Text>
      </div>
    </Header>
  );

  const renderCompanyHeader = () => (
    <CompanyHeader>
      <CompanyInfoWrapper>
        { renderCompanyLogo() }
        <CompanyNameContainer>
          { renderSymbol() }
          <NotationCountryFlagContainer>
            { renderNotationCorpActionBadges() }
            { renderCountryFlag() }
            { renderSuspendedDelistedBadges() }
          </NotationCountryFlagContainer>
          { renderCompanyName() }
          {renderCompanyTags()}
        </CompanyNameContainer>
        { renderExpandButtonToggle() }
      </CompanyInfoWrapper>
      { renderNotationCorpActionBar(isCompanyStock) }
    </CompanyHeader>
  );

  return isCompanyStock ? renderCompanyHeader() : renderNonCompanyHeader();
}

export default GuestHeader;
